import { chain, compact } from "lodash"
import { getHorizonParameter, getSimulateActionsNode, getActionsBaseParameter, getIncludeNotHeldParameter, getBetaInstrumentParameter, getQueryParameters, getAttributionMethodParameter, getContentSetParameter, getForecastIdParameter, getForecastSecuritiesParameter, getAnalyticsOutputParameter } from "./helpers"
import { groupedRiskContributorsQueryFragment, riskContributorsQueryFragment, riskQueryFragment } from "./risk"
import { contentSetExposureContributorsQueryFragment, contentSetExposureQueryFragment, exposureContributorsQueryFragment, exposureQueryFragment, groupedContentSetExposureContributorsQueryFragment, groupedExposureContributorsQueryFragment } from "./exposure"
import { betaContributorsQueryFragment, betaQueryFragment, dynamicBetaContributorsQueryFragment, dynamicBetaQueryFragment, groupedBetaContributorsQueryFragment, groupedDynamicBetaContributorsQueryFragment } from "./beta"
import { forecastContributorsQueryFragment, groupedForecastContributorsQueryFragment, forecastQueryFragment } from "./forecast"
import { factorSensitivityQueryFragment, factorSensitivityContributorsQueryFragment, groupedFactorSensitivityContributorsQueryFragment } from "./factorSensitivity"
import { compositionQueryFragment, groupedCompositionQueryFragment, positionHoldingsQueryFragment, positionsDeltaQueryFragment } from "./composition"
import { riskContributorsFactorsQueryFragment, riskFactorsQueryFragment, groupedRiskContributorsFactorsQueryFragment } from "./riskFactors"
import { groupedMarginalContributorsQueryFragment, marginalContributorsQueryFragment } from "./marginal"
import { DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES, requiresRiskFactors } from ".."

export const rebalanceQuery = (parameters) => {
  const { decompositionType, missingDataFiles, classifications, selectedContentSet, betaInstrument, forecastAndHorizon, isIntraday, isInvalidBetaInstrument, groupingOptions, isOpPro, historicalFactorSensitivityUnavailable } = parameters

  const actionsBaseParameter = getActionsBaseParameter(parameters)
  const includeNotHeldParameter = getIncludeNotHeldParameter(parameters)
  const horizonParameter = getHorizonParameter(forecastAndHorizon, parameters)
  const betaInstrumentParameter = getBetaInstrumentParameter(betaInstrument, parameters)
  const attributionMethodParameter = getAttributionMethodParameter(parameters)
  const contentSetParameter = getContentSetParameter(selectedContentSet)
  const analyticsOuputParameter = getAnalyticsOutputParameter(parameters)

  const allParameters = compact([
    analyticsOuputParameter,
    actionsBaseParameter,
    includeNotHeldParameter,
    horizonParameter,
    decompositionType === "beta" && betaInstrumentParameter,
    attributionMethodParameter,
    contentSetParameter,
  ])

  let forecastIdParameter, forecastSecuritiesParameter
  if (!isIntraday) {
    forecastIdParameter = getForecastIdParameter(forecastAndHorizon)
    if (forecastIdParameter) {
      allParameters.push(forecastIdParameter)
    } else {
      forecastSecuritiesParameter = getForecastSecuritiesParameter(parameters)
      allParameters.push(forecastSecuritiesParameter)
    }
  }
  const includeForecast = (forecastIdParameter || forecastSecuritiesParameter) && !isIntraday
  const fragments = [
    positionHoldingsQueryFragment(),
    positionsDeltaQueryFragment(),
    compositionQueryFragment(classifications, [includeNotHeldParameter]),
    riskQueryFragment(parameters),
    riskContributorsQueryFragment([includeNotHeldParameter, attributionMethodParameter], parameters),
    includeForecast && forecastContributorsQueryFragment([includeNotHeldParameter, forecastIdParameter, forecastSecuritiesParameter]),
    groupingOptions && groupedCompositionQueryFragment([includeNotHeldParameter]),
    groupingOptions && groupedRiskContributorsQueryFragment([includeNotHeldParameter, attributionMethodParameter], parameters),
    includeForecast && groupingOptions && groupedForecastContributorsQueryFragment([includeNotHeldParameter, forecastIdParameter, forecastSecuritiesParameter]),
  ]

  if (requiresRiskFactors(decompositionType)) {
    fragments.push(
      riskFactorsQueryFragment(parameters),
      riskContributorsFactorsQueryFragment([includeNotHeldParameter, attributionMethodParameter], parameters),
    )
    if (groupingOptions) {
      fragments.push(
        groupedRiskContributorsFactorsQueryFragment([includeNotHeldParameter, attributionMethodParameter], parameters),
      )
    }
  }

  if (decompositionType === "marginal" && !isOpPro) {
    fragments.push(
      marginalContributorsQueryFragment([includeNotHeldParameter, attributionMethodParameter], parameters),
    )
    if (groupingOptions) {
      fragments.push(
        groupedMarginalContributorsQueryFragment([includeNotHeldParameter, attributionMethodParameter], parameters),
      )
    }
  }

  if (decompositionType === "exposure") {
    fragments.push(
      exposureQueryFragment(),
      exposureContributorsQueryFragment([includeNotHeldParameter]),
    )
    if (groupingOptions) {
      fragments.push(
        groupedExposureContributorsQueryFragment([includeNotHeldParameter]),
      )
    }
  }

  if (decompositionType === "beta") {
    const includeDynamicBeta = !isIntraday && !isInvalidBetaInstrument
    fragments.push(
      betaQueryFragment(missingDataFiles),
      betaContributorsQueryFragment(missingDataFiles, [includeNotHeldParameter]),
      includeDynamicBeta && dynamicBetaQueryFragment(betaInstrument),
      includeDynamicBeta && dynamicBetaContributorsQueryFragment(betaInstrument, [betaInstrumentParameter, includeNotHeldParameter]),
    )
    if (groupingOptions) {
      fragments.push(
        groupedBetaContributorsQueryFragment(missingDataFiles, [includeNotHeldParameter]),
        includeDynamicBeta && groupedDynamicBetaContributorsQueryFragment(betaInstrument, [betaInstrumentParameter, includeNotHeldParameter]),
      )
    }
  }

  const factorSensititivityType = DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType]
  if (decompositionType === "predictedFactorSensitivity" && !isOpPro) {
    fragments.push(
      factorSensitivityQueryFragment(factorSensititivityType, parameters),
      factorSensitivityContributorsQueryFragment(factorSensititivityType, [includeNotHeldParameter], parameters),
    )
    if (groupingOptions) {
      fragments.push(
        groupedFactorSensitivityContributorsQueryFragment(factorSensititivityType, [includeNotHeldParameter], parameters),
      )
    }
  }

  if (decompositionType === "historicalFactorSensitivity" && !historicalFactorSensitivityUnavailable) {
    fragments.push(
      factorSensitivityQueryFragment(factorSensititivityType, parameters),
      factorSensitivityContributorsQueryFragment(factorSensititivityType, [includeNotHeldParameter], parameters),
    )
    if (groupingOptions) {
      fragments.push(
        groupedFactorSensitivityContributorsQueryFragment(factorSensititivityType, [includeNotHeldParameter], parameters),
      )
    }
  }

  if (selectedContentSet) {
    fragments.push(
      contentSetExposureQueryFragment(selectedContentSet),
      contentSetExposureContributorsQueryFragment(selectedContentSet, [includeNotHeldParameter, contentSetParameter]),
    )
    if (groupingOptions) {
      fragments.push(
        groupedContentSetExposureContributorsQueryFragment(selectedContentSet, [includeNotHeldParameter, contentSetParameter]),
      )
    }
  }

  return `query (${getQueryParameters(allParameters, groupingOptions)}) {
    model (id: $modelId) {
      ${getSimulateActionsNode([analyticsOuputParameter, actionsBaseParameter])} {
        ${chain(fragments)
          .compact()
          .join("\n")
          .value()}
      }
    }
  }`
}

export function rebalanceOnlyForecast(parameters) {
  const { forecastAndHorizon } = parameters
  const actionsBaseParameter = getActionsBaseParameter(parameters)
  const includeNotHeldParameter = getIncludeNotHeldParameter(parameters)
  const forecastIdParameter = getForecastIdParameter(forecastAndHorizon)
  const forecastSecuritiesParameter = forecastIdParameter ? null : getForecastSecuritiesParameter(parameters)
  const horizonParameter = getHorizonParameter(forecastAndHorizon, parameters)
  const analyticsOuputParameter = getAnalyticsOutputParameter(parameters)
  const { isIntraday, groupingOptions } = parameters

  const includeForecast = (forecastIdParameter || forecastSecuritiesParameter) && !isIntraday
  return `query (${getQueryParameters([analyticsOuputParameter, actionsBaseParameter, includeNotHeldParameter, forecastIdParameter, forecastSecuritiesParameter, horizonParameter], groupingOptions)}) {
    model (id: $modelId) {
      ${getSimulateActionsNode([analyticsOuputParameter, actionsBaseParameter])} {
        ${includeForecast ? `
          ${forecastQueryFragment(forecastIdParameter)}
          ${forecastContributorsQueryFragment([includeNotHeldParameter, forecastIdParameter, forecastSecuritiesParameter])}
        ` : ""}
        ${!groupingOptions ? "" : `
          ${groupedForecastContributorsQueryFragment([includeNotHeldParameter, forecastIdParameter, forecastSecuritiesParameter])}
        `}
      }
    }
  }`
}
